// Dashboard page render — the default landing view. Takes `orders`,
// `products`, `settings` as explicit arguments (same dependency-injection
// pattern as the other extracted page/service modules) rather than closing
// over the app's mutable ORDERS/PRODUCTS/SETTINGS globals.
//
// One deliberate simplification, verified rather than assumed safe: the
// original inline packagingCostForOrder()/inventoryAlertCount() wrappers
// each called ensureInventorySettings() defensively on every invocation
// (re-normalizing settings.packagingItems / products stock defaults).
// ensureInventorySettings() already runs unconditionally at module load and
// again on every importState()/clearState() — i.e. before any real
// render() call can ever happen (see the SECURITY comment above
// window.ML_CRM in public/index.html, which explains #content stays empty
// pre-auth for exactly this reason) — so by the time viewDashboard() is
// actually invoked, settings/products are already normalized and the extra
// defensive call is a no-op. Confirmed with a zero-diff comparison against
// the original (pre-normalized fixtures, matching every real call site)
// before removal.
//
// Every onclick="..."/href="#" string in the returned HTML (openOrder(...),
// go('wallet'), goFilteredGroup(...), ...) is untouched — this only moves
// what builds the string. See REFACTOR_SECURITY_REPORT.md, Phase 2 section.
import { esc, money, fmtDate, curMonth, monthLabel } from '../utils/format.js';
import { ic } from '../utils/icons.js';
import { kpi, badge, productDisplayHtml } from '../components/labels.js';
import { orderTotal, packagingCostForOrder } from '../services/pricing.js';
import { monthStats } from '../services/wallet.js';
import { inventoryAlertCount } from '../services/inventory.js';
import { byNew } from '../services/orders.js';

function kpiClick(l, v, i, cls, fn) {
  return `<div class="kpi click ${cls || ''}" onclick="${fn}"><span class="kic">${ic(i, 26)}</span><div class="lab">${l}</div><div class="val">${v}</div></div>`;
}

export function viewDashboard(orders, products, settings) {
  const o = orders;
  const cnt = k => o.filter(x => x.status === k).length;
  let h = '';

  const walletTransactions = Array.isArray(settings.walletTransactions) ? settings.walletTransactions : [];
  const packagingCost = () => packagingCostForOrder(settings.packagingItems, settings.costPackaging);

  // 1) ARGENT CE MOIS-CI
  const m = curMonth();
  const ms = monthStats(m, orders, products, walletTransactions, packagingCost);
  h += '<div class="sec-title">' + ic('coins') + 'Ce mois-ci — ' + monthLabel(m) + '</div>';
  h += '<div class="grid k4">';
  h += kpi('Chiffre d\'affaires', money(ms.revenue), 'coins', 'brand');
  h += kpi('Dépenses pub', money(ms.ads), 'mega', '');
  h += kpi('Bénéfice net', money(ms.net), 'coins', ms.net >= 0 ? 'good' : 'bad');
  h += kpi('Commandes livrées', ms.count, 'checkcircle', '');
  h += '</div>';
  h += '<p class="hint" style="margin-top:8px">Le bénéfice net = CA livré − coût produit − packaging − livraison − pub et frais du mois. <a href="#" onclick="go(\'wallet\');return false">Ouvrir le portefeuille</a></p>';
  const stockAlerts = inventoryAlertCount(products, settings.packagingItems);
  const unpaid = o.filter(x => x.status === 'livree' && x.paymentStatus !== 'received').length, missingPrice = products.filter(p => Number(p.price || 0) <= 0).length;
  h += `<div class="action-center"><div class="action-card"><div class="action-icon">${ic('coins', 19)}</div><div><b>${unpaid} paiement(s) à rapprocher</b><p>Commandes livrées dont l'argent n'est pas encore affecté à un compte.</p><button class="btn ghost" onclick="go('wallet')">Ouvrir le portefeuille</button></div></div><div class="action-card"><div class="action-icon">${ic('package', 19)}</div><div><b>${stockAlerts} alerte(s) de stock</b><p>Produits et emballages à vérifier avant les prochaines expéditions.</p><button class="btn ghost" onclick="go('inventory')">Vérifier le stock</button></div></div><div class="action-card"><div class="action-icon">${ic('coins', 19)}</div><div><b>${missingPrice} prix à compléter</b><p>Complétez les produits à 0 DH avant de créer une nouvelle commande.</p><button class="btn ghost" onclick="go('${missingPrice ? 'products' : 'calculator'}')">${missingPrice ? 'Voir les produits' : 'Calculer un prix'}</button></div></div></div>`;

  // 2) À TRAITER
  h += '<div class="sec-title">' + ic('clock') + 'À traiter</div>';
  const toConfirm = cnt('nouvelle') + cnt('a_confirmer') + cnt('incomplete');
  const toShip = cnt('confirmee') + cnt('prete');
  const inTransit = cnt('envoyee') + cnt('transit');
  h += '<div class="grid k3">';
  h += kpiClick('À confirmer', toConfirm, 'clock', toConfirm ? 'bad' : '', "goFilteredGroup(['nouvelle','a_confirmer','incomplete'])");
  h += kpiClick('À expédier', toShip, 'truck', '', "goFilteredGroup(['confirmee','prete'])");
  h += kpiClick('En livraison', inTransit, 'package', '', "goFilteredGroup(['envoyee','transit'])");
  h += '</div>';

  // 3) LIVRAISON (santé)
  const livree = cnt('livree'), retour = cnt('retournee') + cnt('refusee'), fin = livree + retour;
  const sr = fin ? Math.round(livree / fin * 100) : 0, rr = fin ? Math.round(retour / fin * 100) : 0;
  h += '<div class="sec-title">' + ic('truck') + 'Livraison</div>';
  h += '<div class="grid k4">';
  h += kpi('Taux de livraison', sr + '%', 'checkcircle', 'good');
  h += kpi('Taux de retour', rr + '%', 'undo', rr > 20 ? 'bad' : '');
  h += kpi('Livrées (total)', livree, 'check', '');
  h += kpi('Montant en livraison', money(o.filter(x => ['envoyee', 'transit'].includes(x.status)).reduce((s, x) => s + orderTotal(products, x), 0)), 'truck', '');
  h += '</div>';

  // 4) DERNIÈRES COMMANDES
  h += '<div class="sec-title">' + ic('package') + 'Dernières commandes</div>';
  const recent = [...o].sort(byNew).slice(0, 8);
  h += '<div class="card"><div class="bd" style="padding:0;overflow:auto">';
  if (recent.length) { h += '<table><tbody>' + recent.map(r => `<tr style="cursor:pointer" onclick="openOrder('${r.id}')"><td class="bidi"><b>${esc(r.name || '—')}</b><br><span class="hint">${esc(r.phone || '')} · ${esc(r.city || '')}</span></td><td class="bidi">${productDisplayHtml(r, products)}</td><td><b>${money(orderTotal(products, r))}</b></td><td>${badge(r.status)}</td><td>${fmtDate(r.orderDate)}</td></tr>`).join('') + '</tbody></table>'; }
  else h += '<div class="empty">Aucune commande. Cliquez « Nouvelle commande » pour démarrer.</div>';
  h += '</div></div>';
  return h;
}
